import React from 'react'
import { observer } from 'mobx-react-lite'
import clsx from 'clsx'

import { addMinutes, fromUnixTime } from 'date-fns'

import { FileCopy, Search } from '@material-ui/icons'
import { makeStyles } from '@material-ui/core/styles'
import { IconButton, Link, TableCell, TableRow } from '@material-ui/core'

import { useStore } from '../hooks/storeHook'
import { useMessages } from '../hooks/snackbarHooks'

import { appConfig } from '../common/devUtils'
import { formatDateForGrafana, formatDateTimeUnix } from '../common/dateTimeUtility'

import { integrationTypeNames } from '../types/integrationTypeUtils'
import { SyncLogModel } from '../server/mpsklad_core/Models/SyncLogModel'
import { SyncLogSeverity } from '../server/mpsklad_core/Entity/SyncLogSeverity'

export const SyncLogRow =
  observer(
    ({log}: {log: SyncLogModel}) => {
      const classes = useStyles()

      const {user} = useStore()
      const {showInfo} = useMessages()

      const createdAtText = formatDateTimeUnix(log.createdAt)

      const onDateClick = async () => {
        await navigator.clipboard.writeText(createdAtText)
        showInfo('Дата скопирована')
      }

      const onMessageClick = async () => {
        await navigator.clipboard.writeText(log.message)
        showInfo('Сообщение скопировано')
      }

      const onIdClick = async () => {
        await navigator.clipboard.writeText(log.id)
        showInfo('Id скопирован')
      }

      return (
        <TableRow>
          <TableCell className={'table-border'}>
            <span className={classes.text}>{createdAtText}</span>

            <IconButton title="Копировать" size="small" className={classes.iconButton} onClick={onDateClick}>
              <FileCopy fontSize="small"/>
            </IconButton>
          </TableCell>

          <TableCell
            className={clsx(
              'table-border',
              log.severity === SyncLogSeverity.Info && classes.info,
              log.severity === SyncLogSeverity.Warning && classes.warning,
              log.severity === SyncLogSeverity.Error && classes.error
            )}>
            <span className={classes.text}>
              {syncLogSeverityNames[log.severity]} | {integrationTypeNames[log.integrationType]} #{log.accountId}
            </span>
          </TableCell>

          <TableCell className={'table-border'}>
            <span className={classes.text}>{log.message}</span>

            <IconButton title="Копировать" size="small" className={classes.iconButton} onClick={onMessageClick}>
              <FileCopy fontSize="small"/>
            </IconButton>
          </TableCell>

          <TableCell className={'table-border'}>
            <span className={classes.text}>{log.id}</span>

            <IconButton title="Копировать" size="small" className={classes.iconButton} onClick={onIdClick}>
              <FileCopy fontSize="small"/>
            </IconButton>

            {
              log.severity === SyncLogSeverity.Error && (user?.isSuperAdmin || user?.isImpersonating) &&
              <Link
                title="Найти в Grafana"
                target="_blank"
                href={makeGrafanaFindErrorUrl(log)}
                className={clsx(classes.iconButton, classes.iconLink)}
              >
                <Search fontSize="small" color="secondary"/>
              </Link>
            }
          </TableCell>
        </TableRow>
      )
    })

export const syncLogSeverityNames: {
  readonly [K in SyncLogSeverity]: string
} = {
  [SyncLogSeverity.Info]: 'Инфо',
  [SyncLogSeverity.Warning]: 'Предупреждение',
  [SyncLogSeverity.Error]: 'Ошибка'
}

const makeGrafanaFindErrorUrl =
  (log: SyncLogModel) => {
    const dateFrom = addMinutes(fromUnixTime(log.createdAt), -5)
    const dateTo = addMinutes(fromUnixTime(log.createdAt), 5)

    const resultUrl = new URL(appConfig.grafanaErrorUrl)
    resultUrl.searchParams.append('from', formatDateForGrafana(dateFrom))
    resultUrl.searchParams.append('to', formatDateForGrafana(dateTo))
    resultUrl.searchParams.append('timezone', 'browser')
    resultUrl.searchParams.append('var-errorLogId', log.id)

    return resultUrl.toString()
  }

const useStyles = makeStyles(
  ({palette}) => ({
    text: {
      fontSize: 12
    },
    error: {
      background: palette.mpSkladSecondary.lightRed
    },
    warning: {
      background: palette.mpSkladSecondary.orange
    },
    info: {
      background: palette.mpSkladSecondary.green
    },
    iconButton: {
      marginLeft: 5,
      opacity: 0,
      'td:hover &': {
        opacity: 1
      }
    },
    iconLink: {
      verticalAlign: 'middle'
    }
  })
)